import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import { tx } from "@/lib/i18nDict";
import { fmtDateNum } from "@/lib/dateFormat";

const NAVY = [11, 26, 51];
let GOLD = [255, 209, 0];
let SECONDARY = [11, 26, 51];

// Current signatory (name · role) for PDF traceability — set from AuthContext.
let _signatory = "";
export function setSignatory(user) {
  if (!user || typeof user !== "object") { _signatory = ""; return; }
  const role = user.is_admin ? "Admin" : "Staff";
  _signatory = `${user.name || user.email || "Staff"} · ${role}`;
}
export function getSignatory() { return _signatory; }

let _crestCache;
// Context-specific official Parma crests (high-def, clean). Populated by pdfBrand.
let CRESTS = { white: "", blue: "", yellow: "", full: "" };

// White-label override (set from AuthContext for non-default clubs). When a club has
// its own crest/color, exports use them; for the founding club these stay empty and
// nothing changes.
let _brandCrestUrl = "";
let _brandAccentHex = "";
let _brandSecondaryHex = "";
let _brandClub = "";
export function setBrandOverride(o) {
  _brandCrestUrl = (o && o.crestUrl) || "";
  _brandAccentHex = (o && o.primaryColor) || "";
  _brandSecondaryHex = (o && o.secondaryColor) || "";
  _brandClub = (o && o.clubName) || "";
  _crestCache = undefined; CRESTS = { white: "", blue: "", yellow: "", full: "" };
}
// White-label document name: the club's own name, or Parma for the founding club.
export function pdfBrandName() { return (_brandClub || "PARMA CALCIO 1913 WOMEN").toUpperCase(); }
export function pdfBrandShort() { const n = _brandClub || "Parma"; return n.split(/\s+/)[0]; }
export function pdfBrandLabel(suffix = "TECHNICAL HUB") { return `${pdfBrandName()} · ${suffix}`; }
function hexToRgb(hex, fallback) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return fallback;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export function getBrandAccentHex() { return _brandAccentHex || "#FFD100"; }
export function getBrandAccentRGB() { return hexToRgb(_brandAccentHex, [255, 209, 0]); }
export function getBrandSecondaryHex() { return _brandSecondaryHex || getBrandAccentHex(); }
export function getBrandSecondaryRGB() { return hexToRgb(_brandSecondaryHex, getBrandAccentRGB()); }
// Refresh module-level brand colours at the start of each export entry point.
function refreshBrand() { GOLD = getBrandAccentRGB(); SECONDARY = getBrandSecondaryRGB(); }

export async function loadCrest() {
  if (_brandCrestUrl) {
    const c = await loadAsset(_brandCrestUrl);
    if (c) { if (!CRESTS.blue) CRESTS.blue = c; return c; }
  }
  // Default standalone crest = WHITE (used on the navy header bar). Also warm the
  // BLUE crest cache so watermarks (drawn on white pages) stay visible even for
  // exports that don't call pdfBrand.
  const [white, blue] = await Promise.all([
    loadAsset("/assets/crest_white_official.png"),
    loadAsset("/assets/crest_blue_official.png"),
  ]);
  if (!CRESTS.blue) CRESTS.blue = blue;
  return white;
}

// Brand-aware asset getters: white-label clubs use THEIR crest everywhere and NO Parma
// stadium background. The founding (default) club keeps its official Parma assets.
export function isWhiteLabel() { return !!_brandCrestUrl; }
export async function brandCrestFull(fallback = null) { return _brandCrestUrl ? (await loadAsset(_brandCrestUrl)) || fallback : (await loadAsset("/assets/crest_full.png").catch(() => fallback)); }
export async function brandCrestWhite(fallback = null) { return _brandCrestUrl ? (await loadAsset(_brandCrestUrl)) || fallback : (await loadAsset("/assets/crest_white_official.png").catch(() => fallback)); }
export async function brandMark(fallback = null) { return _brandCrestUrl ? (await loadAsset(_brandCrestUrl)) || fallback : (await loadAsset("/assets/crest_yellow.png").catch(() => fallback)); }
export async function brandPageBg() { return _brandCrestUrl ? null : (await loadAsset("/assets/bg_tardini.jpg").catch(() => null)); }
// Ask the user, at export time, whether they want a full dossier (with cover) or a simple PDF.
// Returns true for dossier-with-cover, false for simple. (Single confirm = zero UI deps.)
export function askDossierCover() {
  return window.confirm(
    tx("Esportare come DOSSIER con copertina?\n\nOK = Dossier con copertina\nAnnulla = PDF semplice")
  );
}
// Ask (every export): cover-or-simple + dossier author name + author role.
// Uses a branded modal (registered by ExportOptionsHost) when available, else falls
// back to native dialogs. Always returns a Promise<{cover,author,role}|null>. null = cancelled.
let _exportOptionsHandler = null;
export function setExportOptionsHandler(fn) { _exportOptionsHandler = fn; }
export function askExportOptions(opts) {
  if (_exportOptionsHandler) return _exportOptionsHandler(opts);
  const cover = askDossierCover();
  let prevA = "", prevR = "";
  try { prevA = localStorage.getItem("pc_export_author") || ""; prevR = localStorage.getItem("pc_export_role") || ""; } catch (e) { /* ignore */ }
  const author = (window.prompt(tx("Autore del dossier (nome e cognome):"), prevA) || "").trim();
  const role = (window.prompt(tx("Ruolo dell'autore:"), prevR) || "").trim();
  try { if (author) localStorage.setItem("pc_export_author", author); if (role) localStorage.setItem("pc_export_role", role); } catch (e) { /* ignore */ }
  return Promise.resolve({ cover, author, role });
}

// Single finalisation point for every PDF — downloads the generated file.
// Returns false so callers keep showing their single success toast.
export async function finalizePdf(doc, filename) {
  const name = String(filename || "document").endsWith(".pdf") ? filename : `${filename}.pdf`;
  doc.save(name);
  return false;
}

const _assetCache = {};
export async function loadAsset(path) {
  if (_assetCache[path] !== undefined) return _assetCache[path];
  try {
    const res = await fetch(path);
    const blob = await res.blob();
    _assetCache[path] = await new Promise((resolve) => { const r = new FileReader(); r.onloadend = () => resolve(r.result); r.readAsDataURL(blob); });
  } catch (e) { _assetCache[path] = ""; }
  return _assetCache[path];
}

// Crop an image to EXACTLY the target aspect (cover, centred) so it never stretches.
// Fills the canvas with navy first so any transparent areas match the desktop blue
// (never black) and the exported photo keeps clean edges.
export function coverCrop(src, w, h) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#1c4b8a"; ctx.fillRect(0, 0, w, h);
      const s = Math.max(w / img.width, h / img.height);
      const dw = img.width * s, dh = img.height * s;
      ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
      resolve(c.toDataURL("image/jpeg", 0.95));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// Produce a clean, print-ready player photo for PDFs. Supersamples for crisp downscaling;
// auto-detects cut-out (transparent) silhouettes and SOFTENS their alpha edge so the
// silhouette blends smoothly (no pixelated/jagged border), then mattes onto a solid bg
// (matching the desktop navy) so nothing ever exports on black. Fully-opaque photos keep
// their sharp rectangular edges (no unwanted feather).
export function cleanPhoto(src, { bg = "#ffffff", maxSide = 900, feather = 1.6 } = {}) {
  return new Promise((resolve) => {
    if (!src) return resolve(src);
    const img = new Image();
    img.onload = () => {
      try {
        const SS = 2; // supersample factor
        const s = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * s * SS));
        const h = Math.max(1, Math.round(img.height * s * SS));
        const t = document.createElement("canvas"); t.width = w; t.height = h;
        const tx = t.getContext("2d");
        tx.imageSmoothingEnabled = true; tx.imageSmoothingQuality = "high";
        tx.drawImage(img, 0, 0, w, h);
        // Detect a cut-out silhouette by sampling the alpha channel.
        let cutout = false;
        try {
          const data = tx.getImageData(0, 0, w, h).data;
          let transparent = 0, count = 0;
          for (let i = 3; i < data.length; i += 4 * 7) { count++; if (data[i] < 200) transparent++; }
          cutout = count > 0 && transparent / count > 0.03;
        } catch (e) { cutout = false; }
        if (cutout) {
          // Feather: mask the subject with a slightly blurred copy of its own alpha.
          tx.globalCompositeOperation = "destination-in";
          tx.filter = `blur(${feather * SS}px)`;
          tx.drawImage(t, 0, 0);
          tx.filter = "none"; tx.globalCompositeOperation = "source-over";
        }
        const c = document.createElement("canvas"); c.width = w; c.height = h;
        const ctx = c.getContext("2d");
        ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high";
        ctx.drawImage(t, 0, 0);
        resolve(c.toDataURL("image/jpeg", 0.95));
      } catch (e) { resolve(src); }
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });
}

// Back-compat alias: composite a photo onto a solid navy background (now cleaned + feathered).
export function photoWithBg(src, bg = "#ffffff", maxSide = 900) { return cleanPhoto(src, { bg, maxSide }); }

// Faint full-page stadium background (replaces the plain white). Draw FIRST on each page.
export function pdfPageBg(doc, page) {
  if (!page) return;
  const w = doc.internal.pageSize.getWidth(), h = doc.internal.pageSize.getHeight();
  try {
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.05 }));
    doc.addImage(page, "JPEG", 0, 0, w, h);
    doc.setGState(new doc.GState({ opacity: 1 }));
    doc.restoreGraphicsState();
  } catch (e) { /* ignore */ }
}

export function pdfHeader(doc, title, subtitle, landscape = false, crest = null, banner = null, meta = "") {
  refreshBrand();
  const w = landscape ? 297 : 210;
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, w, 30, "F");
  // brand pinstripes: primary (thick) + secondary (thin)
  doc.setFillColor(...GOLD);
  doc.rect(0, 30, w, 1.8, "F");
  doc.setFillColor(...SECONDARY);
  doc.rect(0, 32.2, w, 0.7, "F");
  let tx = 14;
  if (crest) {
    try {
      const cp = doc.getImageProperties(crest);
      const bw = 16, bh = 16, rr = Math.min(bw / cp.width, bh / cp.height);
      const iw = cp.width * rr, ih = cp.height * rr;
      doc.addImage(crest, "PNG", 12 + (bw - iw) / 2, 6 + (bh - ih) / 2, iw, ih, undefined, "SLOW");
      tx = 33;
    } catch (e) { /* ignore */ }
  }
  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(pdfBrandName(), tx, 11);
  doc.setTextColor(255, 255, 255);
  const availW = (meta ? w - 60 : w - 14) - tx;
  let hFs = 15;
  doc.setFont("helvetica", "bold"); doc.setFontSize(hFs);
  while (doc.getTextWidth(title.toUpperCase()) > availW && hFs > 9) { hFs -= 1; doc.setFontSize(hFs); }
  doc.text(title.toUpperCase(), tx, 20);
  // subtitle: own line under the title (left aligned) so it never collides with the title.
  if (subtitle) {
    doc.setTextColor(210, 220, 235);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(subtitle, tx, 26.5);
  }
  // author · role · date byline: small, top-right, clear of the title.
  if (meta) {
    doc.setTextColor(...GOLD);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(meta, w - 14, 11, { align: "right" });
  }
  return 38;
}

export async function pdfBrand(landscape = false) {
  // White-label club (non-founding): use ITS OWN crest everywhere and NO Parma assets
  // (crest, stadium background). This prevents Parma branding leaking into other clubs' exports.
  if (_brandCrestUrl) {
    const c = await loadAsset(_brandCrestUrl);
    CRESTS = { white: c, blue: c, yellow: c, full: c };
    return { crest: c, crestWhite: c, crestBlue: c, crestFull: c, mark: c, banner: null, header: null, page: null };
  }
  const [white, blue, yellow, full, raw] = await Promise.all([
    loadAsset("/assets/crest_white_official.png"),
    loadAsset("/assets/crest_blue_official.png"),
    loadAsset("/assets/crest_yellow.png"),
    loadAsset("/assets/crest_full.png"),
    loadAsset("/assets/bg_tardini.jpg"),
  ]);
  CRESTS = { white, blue, yellow, full };
  const pw = landscape ? 297 : 210, ph = landscape ? 210 : 297;
  const [header, page] = await Promise.all([
    coverCrop(raw, Math.round(pw * 4), 120),
    coverCrop(raw, Math.round(pw * 2.6), Math.round(ph * 2.6)),
  ]);
  // crest (white) → navy header; crestFull → cover; crestBlue → watermark; mark (yellow) → footer.
  return { crest: white, crestWhite: white, crestBlue: blue, crestFull: full, mark: yellow, banner: header || raw, header: header || raw, page };
}

// Faint centred Parma crest watermark behind card content. Draw AFTER page bg, BEFORE content.
export function pdfWatermark(doc, crest, { opacity = 0.06, landscape = false } = {}) {
  const c = CRESTS.blue || crest;
  if (!c) return;
  try {
    const w = landscape ? 297 : 210, h = landscape ? 210 : 297;
    const cp = doc.getImageProperties(c);
    const iw = w * 0.6, ih = cp.height * (iw / cp.width);
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity }));
    doc.addImage(c, "PNG", (w - iw) / 2, (h - ih) / 2, iw, ih);
    doc.setGState(new doc.GState({ opacity: 1 }));
    doc.restoreGraphicsState();
  } catch (e) { /* ignore */ }
}

// Branded navy footer bar with small crest + "All rights reserved". Call once per page.
export function pdfFooter(doc, { mark = null, label = null, right = "", signature = null, landscape = false } = {}) {
  const w = landscape ? 297 : 210;
  const h = doc.internal.pageSize.getHeight();
  const yBar = h - 8;
  doc.setFillColor(...GOLD); doc.rect(0, yBar - 0.7, w, 0.7, "F");
  doc.setFillColor(...NAVY); doc.rect(0, yBar, w, 8, "F");
  doc.setFontSize(7); doc.setTextColor(...GOLD); doc.setFont("helvetica", "bold");
  doc.text("Technical Hub — All rights reserved. Confidential", 14, yBar + 5.2);
  if (right) {
    let cleaned = String(right).replace(/confidential/ig, "").replace(/all rights reserved/ig, "");
    cleaned = cleaned.replace(/\s*[·•]\s*[·•]\s*/g, " · ").replace(/^[\s·•-]+|[\s·•-]+$/g, "").trim();
    if (cleaned) { doc.setTextColor(225, 225, 225); doc.setFont("helvetica", "normal"); doc.text(cleaned, w - 14, yBar + 5.2, { align: "right" }); }
  }
}

// Faint crest watermark stamped on EVERY page (for exports that don't already
// draw a per-page watermark). Call once before the frame/save.
export function pdfWatermarkAllPages(doc, crest, { opacity = 0.05, skipFirst = false } = {}) {
  const c = CRESTS.blue || crest;
  if (!c) return;
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    if (skipFirst && p === 1) continue;
    doc.setPage(p);
    try {
      const w = doc.internal.pageSize.getWidth(), h = doc.internal.pageSize.getHeight();
      const cp = doc.getImageProperties(c);
      const iw = w * 0.55, ih = cp.height * (iw / cp.width);
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity }));
      doc.addImage(c, "PNG", (w - iw) / 2, (h - ih) / 2, iw, ih);
      doc.setGState(new doc.GState({ opacity: 1 }));
      doc.restoreGraphicsState();
    } catch (e) { /* ignore */ }
  }
}

// Elegant branded cover page coordinated with pdfFrameAllPages (navy field, crest,
// large title, gold rule + meta). Draw as the FIRST page of longer reports.
export function pdfCover(doc, { crest, kicker = null, title = "", subtitle = "", docType = "", note = "", meta = "", date = "", landscape = false } = {}) {
  if (kicker == null) kicker = pdfBrandName();
  refreshBrand();
  const w = landscape ? 297 : 210, h = landscape ? 210 : 297;
  doc.setFillColor(...NAVY); doc.rect(0, 0, w, h, "F");
  doc.setFillColor(...GOLD); doc.rect(0, 0, w, 5, "F"); doc.setFillColor(...SECONDARY); doc.rect(0, h - 5, w, 5, "F");
  // All-white official crest placed directly on the navy field — clean and elegant, no plate.
  const c = CRESTS.white || crest;
  if (c) {
    try {
      const cp = doc.getImageProperties(c);
      const cw = 34, ch = cp.height * (cw / cp.width);
      const cx = (w - cw) / 2, cy = h * 0.17;
      doc.addImage(c, "PNG", cx, cy, cw, ch, undefined, "SLOW");
    } catch (e) { /* ignore */ }
  }
  doc.setTextColor(...GOLD); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text(kicker, w / 2, h * 0.43, { align: "center" });
  // Small document-type label (e.g. PLAYER DOSSIER) kept smaller than the name below it.
  if (docType) { doc.setTextColor(170, 185, 210); doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.text(String(docType).toUpperCase().split("").join("\u2009"), w / 2, h * 0.475, { align: "center" }); }
  // Player / subject name — the dominant element. Adaptive size + measured block so long
  // titles never wrap over the gold rule / subtitle below.
  const titleStr = String(title).toUpperCase();
  const maxTW = w - 36;
  doc.setFont("helvetica", "bold");
  let tFs = 38, tLines = [titleStr];
  for (;;) {
    doc.setFontSize(tFs);
    tLines = doc.splitTextToSize(titleStr, maxTW);
    if (tLines.length <= 2 || tFs <= 18) break;
    tFs -= 3;
  }
  doc.setTextColor(255, 255, 255); doc.setFontSize(tFs);
  const titleTopY = h * 0.54;
  doc.text(tLines, w / 2, titleTopY, { align: "center" });
  const lineH = tFs * 0.3528 * 1.12;
  const ruleY = titleTopY + (tLines.length - 1) * lineH + 6;
  doc.setDrawColor(...GOLD); doc.setLineWidth(0.7); doc.line(w / 2 - 30, ruleY, w / 2 + 30, ruleY);
  let belowY = ruleY + 8;
  if (subtitle) {
    doc.setTextColor(210, 220, 235); doc.setFont("helvetica", "normal"); doc.setFontSize(12);
    const sl = doc.splitTextToSize(String(subtitle), w - 40);
    doc.text(sl, w / 2, belowY, { align: "center" });
    belowY += sl.length * 5 + 2;
  }
  // Matches (or any context note) cited on the cover, wrapped and centred under the subtitle.
  if (note) { doc.setTextColor(190, 200, 215); doc.setFont("helvetica", "normal"); doc.setFontSize(9); const nl = doc.splitTextToSize(String(note), w - 60); doc.text(nl, w / 2, belowY, { align: "center" }); }
  // Author, role (comma-separated) centred; date shown once, small, bottom-right.
  if (meta) { doc.setTextColor(...GOLD); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text(meta, w / 2, h - 22, { align: "center" }); }
  doc.setTextColor(150, 165, 190); doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  doc.text("Technical Hub — All rights reserved. Confidential", w / 2, h - 14, { align: "center" });
  if (date) { doc.setTextColor(150, 165, 190); doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.text(String(date), w - 12, h - 9, { align: "right" }); }
}

// Short axis label for radar charts (drop parenthetical units, cap length)
export const shortLabel = (h) => (h || "").replace(/\s*\(.*?\)\s*/g, "").trim().slice(0, 13);

// Uniform Parma page frame: a single thin navy border with generous white margins.
// (No gold corner ticks — kept clean and elegant.) Call once right before doc.save().
export function pdfFrameAllPages() {
  // Border rectangles removed by request — they were visually redundant.
}

function polygon(doc, pts, style) {
  if (pts.length < 2) return;
  const segs = pts.slice(1).map((p, i) => [p[0] - pts[i][0], p[1] - pts[i][1]]);
  doc.lines(segs, pts[0][0], pts[0][1], [1, 1], style, true);
}

// Radar comparing the player's last match to her role average (100% = role average ring).
// axes: [{ label, pct }] where pct is the metric expressed as % of the role average.
export function pdfRadar(doc, cx, cy, R, axes, title) {
  if (title) { doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...NAVY); doc.text(title, cx, cy - R - 7, { align: "center" }); }
  const n = axes.length;
  if (n < 3) { doc.setFont("helvetica", "italic"); doc.setFontSize(7); doc.setTextColor(130, 130, 130); doc.text("Not enough data yet", cx, cy, { align: "center" }); return; }
  const maxPct = Math.max(140, ...axes.map((a) => a.pct || 0));
  const ang = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  const pt = (pct, i) => [cx + Math.cos(ang(i)) * (pct / maxPct) * R, cy + Math.sin(ang(i)) * (pct / maxPct) * R];
  doc.setLineWidth(0.2); doc.setDrawColor(205, 210, 218);
  [maxPct * 0.5, maxPct].forEach((ring) => polygon(doc, axes.map((_, i) => pt(ring, i)), "S"));
  axes.forEach((_, i) => { const [x, y] = pt(maxPct, i); doc.line(cx, cy, x, y); });
  doc.setDrawColor(...GOLD); doc.setLineWidth(0.6);
  polygon(doc, axes.map((_, i) => pt(100, i)), "S");
  // Season-average polygon (outline only, muted grey) — drawn below the last-match area for immediate comparison.
  const hasAvg = axes.some((a) => Number.isFinite(a.avgPct));
  if (hasAvg) {
    doc.setDrawColor(120, 130, 145); doc.setLineWidth(0.5); doc.setLineDashPattern([0.8, 0.8], 0);
    polygon(doc, axes.map((a, i) => pt(Number.isFinite(a.avgPct) ? a.avgPct : 0, i)), "S");
    doc.setLineDashPattern([], 0);
  }
  try { doc.saveGraphicsState(); doc.setGState(new doc.GState({ opacity: 0.4 })); } catch (e) { /* ignore */ }
  doc.setFillColor(11, 26, 51); doc.setDrawColor(11, 26, 51); doc.setLineWidth(0.8);
  polygon(doc, axes.map((a, i) => pt(a.pct, i)), "FD");
  try { doc.setGState(new doc.GState({ opacity: 1 })); doc.restoreGraphicsState(); } catch (e) { /* ignore */ }
  doc.setFont("helvetica", "bold"); doc.setFontSize(5.4); doc.setTextColor(55, 55, 55);
  const _rfmt = (v) => (v == null || !Number.isFinite(v) ? "" : (Number.isInteger(v) ? String(v) : Number(v).toFixed(1)));
  axes.forEach((a, i) => {
    const [lx, ly] = pt(maxPct * 1.17, i);
    doc.setFont("helvetica", "bold"); doc.setFontSize(5.4); doc.setTextColor(55, 55, 55);
    doc.text(a.label, lx, ly, { align: "center" });
    const vtxt = _rfmt(a.val);
    if (vtxt) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(5.6); doc.setTextColor(...NAVY);
      doc.text(vtxt, lx, ly + 2.7, { align: "center" });
    }
    const atxt = _rfmt(a.avg);
    if (atxt) {
      doc.setFont("helvetica", "normal"); doc.setFontSize(4.8); doc.setTextColor(120, 130, 145);
      doc.text(`ø ${atxt}`, lx, ly + 5.1, { align: "center" });
    }
  });
}

function _hexRgb(h) {
  if (Array.isArray(h)) return h;
  const m = /^#?([0-9a-f]{6})$/i.exec(String(h || "").trim());
  if (!m) return [11, 26, 51];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// Pick black or white text for maximum contrast on a given background colour.
export function readableText(color) {
  const [r, g, b] = _hexRgb(color);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.62 ? [17, 24, 39] : [255, 255, 255];
}

// Multi-series comparison radar (each axis normalised 0–100). series: [{name,color,values[]}].
export function drawCompareRadar(doc, cx, cy, R, axes, series, title) {
  const n = axes.length;
  if (title) { doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...NAVY); doc.text(title, cx, cy - R - 11, { align: "center" }); }
  if (n < 3) return cy + R;
  const ang = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  const pt = (pct, i) => [cx + Math.cos(ang(i)) * (pct / 100) * R, cy + Math.sin(ang(i)) * (pct / 100) * R];
  // grid rings + spokes
  doc.setLineWidth(0.2); doc.setDrawColor(205, 210, 218);
  [25, 50, 75, 100].forEach((ring) => polygon(doc, axes.map((_, i) => pt(ring, i)), "S"));
  axes.forEach((_, i) => { const [x, y] = pt(100, i); doc.line(cx, cy, x, y); });
  // series polygons
  series.forEach((s) => {
    const rgb = _hexRgb(s.color);
    const pts = axes.map((_, i) => pt(Math.max(0, Math.min(100, Number(s.values[i]) || 0)), i));
    try { doc.saveGraphicsState(); doc.setGState(new doc.GState({ opacity: 0.16 })); } catch (e) { /* ignore */ }
    doc.setFillColor(...rgb); polygon(doc, pts, "F");
    try { doc.setGState(new doc.GState({ opacity: 1 })); doc.restoreGraphicsState(); } catch (e) { /* ignore */ }
    doc.setDrawColor(...rgb); doc.setLineWidth(0.7); polygon(doc, pts, "S");
    doc.setFillColor(...rgb); pts.forEach(([x, y]) => doc.circle(x, y, 0.7, "F"));
  });
  // axis labels (just outside the grid)
  doc.setFont("helvetica", "bold"); doc.setFontSize(5.2); doc.setTextColor(70, 70, 70);
  axes.forEach((a, i) => { const [lx, ly] = pt(120, i); const lbl = (typeof a === "string" ? a : a.label) || ""; doc.text(lbl.length > 16 ? lbl.slice(0, 15) + "…" : lbl, lx, ly, { align: "center" }); });
  // legend — placed clearly BELOW the axis labels so player names never overlap the chart/labels.
  let ly = cy + R + 12;
  doc.setFontSize(6.4);
  series.forEach((s) => {
    const rgb = _hexRgb(s.color);
    doc.setFillColor(...rgb); doc.rect(cx - R + 2, ly - 2.2, 3, 3, "F");
    doc.setTextColor(60, 60, 60); doc.setFont("helvetica", "bold");
    doc.text(String(s.name || "").slice(0, 28), cx - R + 6.5, ly, { baseline: "middle" });
    ly += 4.2;
  });
  return ly + 2;
}

export function pdfSectionTitle(doc, text, y) {
  refreshBrand();
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...NAVY);
  doc.rect(14, y - 4, w - 28, 7, "F");
  doc.setFillColor(...GOLD);
  doc.rect(14, y - 4, 1.8, 7, "F");
  doc.setFillColor(...SECONDARY);
  doc.rect(14, y + 2.4, w - 28, 0.6, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(text, 18, y + 0.9);
  return y + 10;
}

// Detect a genuine computed "totals" row (a sum/calculation), e.g. "TOTALE (4)", "Balance", "Saldo".
// English metric NAMES like "Total Distance" / "Total Time" are stats, not sums → NOT bolded.
const _isTotalRow = (raw) => {
  const first = String((Array.isArray(raw) ? raw[0] : "") ?? "").trim();
  return /^\s*(totale|balance|saldo)\b/i.test(first);
};

export function pdfTable(doc, head, body, startY, opts = {}) {
  const { leaderCells, darkBody, headColors, cellImages, didDrawCell: extraDraw, ...rest } = opts;
  autoTable(doc, {
    head: [head], body, startY, theme: "grid",
    styles: { fontSize: 8, cellPadding: 1.8, textColor: [30, 30, 30] },
    headStyles: { fillColor: NAVY, textColor: GOLD, fontStyle: "bold" },
    bodyStyles: darkBody ? { fillColor: [222, 229, 238] } : undefined,
    alternateRowStyles: { fillColor: darkBody ? [205, 214, 227] : [245, 247, 250] },
    didDrawCell: (data) => {
      if (cellImages && data.section === "body") {
        const im = cellImages.find((c) => c.row === data.row.index && c.col === data.column.index);
        if (im && im.img) {
          const ih = Math.min(im.h || 4.4, data.cell.height - 1.2);
          const iw = ih * (im.ratio || 1.78);
          const yy = data.cell.y + (data.cell.height - ih) / 2;
          const xx = im.align === "right" ? data.cell.x + data.cell.width - iw - 1.2 : data.cell.x + 1.2;
          try { doc.addImage(im.img, "PNG", xx, yy, iw, ih, undefined, "FAST"); } catch (e) { /* noop */ }
        }
      }
      if (extraDraw) extraDraw(data);
    },
    didParseCell: (data) => {
      // Team-coloured header cells: tint a column header with its club social colour.
      if (data.section === "head" && headColors) {
        const c = headColors[data.column.index];
        if (c) { const rgb = _hexRgb(c); data.cell.styles.fillColor = rgb; data.cell.styles.textColor = readableText(rgb); }
      }
      if (data.section !== "body") return;
      // Always bold totals rows (navy text on a light-blue band).
      if (_isTotalRow(data.row.raw)) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [223, 235, 250];
        data.cell.styles.textColor = [10, 30, 70];
      }
      // Leader highlighting (Compare) takes precedence on its winning cells.
      if (leaderCells && data.column.index >= 1) {
        const winners = leaderCells[data.row.index];
        if (winners && winners.includes(data.column.index - 1)) {
          data.cell.styles.fillColor = [34, 197, 94];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
        }
      }
    },
    margin: { top: 36, left: 14, right: 14 },
    // Prefer keeping the whole table on one page; if it can't fit, let it flow
    // onto the next page normally, repeating the full header and never cutting a row mid-height.
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    showHead: "everyPage",
    ...rest,
  });
  return doc.lastAutoTable.finalY + 8;
}

// Horizontal bar chart. bars: [{label, value, color(hex)}]. Returns bottom y.
export function drawBarChart(doc, x, y, w, bars, { rowH = 9, labelW = 62, unit = "€" } = {}) {
  const max = Math.max(1, ...bars.map((b) => Number(b.value) || 0));
  const valW = 26;
  const barArea = w - labelW - valW;
  doc.setFont("helvetica", "normal");
  bars.forEach((b, i) => {
    const cy = y + i * rowH;
    doc.setFontSize(7.5); doc.setTextColor(40, 40, 40);
    doc.text(String(b.label).length > 30 ? String(b.label).slice(0, 29) + "…" : String(b.label), x, cy + 4);
    doc.setFillColor(230, 233, 238); doc.rect(x + labelW, cy, barArea, 5.4, "F");
    const rgb = _hexRgb(b.color || "#123a6b");
    const bw = Math.max(0.5, (Number(b.value) || 0) / max * barArea);
    doc.setFillColor(...rgb); doc.rect(x + labelW, cy, bw, 5.4, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(20, 20, 20);
    const v = (Number(b.value) || 0).toLocaleString("it-IT");
    doc.text(`${v}${unit ? " " + unit : ""}`, x + labelW + barArea + 2, cy + 4);
    doc.setFont("helvetica", "normal");
  });
  return y + bars.length * rowH + 2;
}

// Multi-series line chart (trend across matchdays). series: [{name,color,points:[num|null],dashed}]
export function drawLineChart(doc, x, y, w, h, { labels = [], series = [], baseline = null } = {}) {
  const padL = 12, padB = 9, padT = 3, padR = 4;
  const plotX = x + padL, plotY = y + padT, plotW = w - padL - padR, plotH = h - padT - padB;
  const vals = [];
  series.forEach((s) => (s.points || []).forEach((p) => { if (p != null && Number.isFinite(Number(p))) vals.push(Number(p)); }));
  if (baseline != null) vals.push(baseline);
  let lo = vals.length ? Math.min(...vals) : 0, hi = vals.length ? Math.max(...vals) : 1;
  if (lo === hi) { lo -= 1; hi += 1; }
  const pad = (hi - lo) * 0.12 || 1; lo -= pad; hi += pad;
  const sx = (i) => plotX + (labels.length <= 1 ? plotW / 2 : (i / (labels.length - 1)) * plotW);
  const sy = (v) => plotY + plotH - ((v - lo) / (hi - lo)) * plotH;
  // axes + gridlines
  doc.setDrawColor(210, 214, 220); doc.setLineWidth(0.2);
  for (let g = 0; g <= 4; g++) { const gy = plotY + (g / 4) * plotH; doc.line(plotX, gy, plotX + plotW, gy); doc.setFontSize(5.4); doc.setTextColor(120, 120, 120); doc.text(String(Math.round(hi - (g / 4) * (hi - lo))), plotX - 1.5, gy + 1.2, { align: "right" }); }
  // baseline (league average reference)
  if (baseline != null) { doc.setDrawColor(255, 176, 0); doc.setLineWidth(0.5); doc.setLineDashPattern([1.4, 1.2], 0); const by = sy(baseline); doc.line(plotX, by, plotX + plotW, by); doc.setLineDashPattern([], 0); }
  // x labels
  doc.setFontSize(5.6); doc.setTextColor(90, 90, 90); doc.setFont("helvetica", "normal");
  labels.forEach((l, i) => doc.text(String(l), sx(i), plotY + plotH + 5, { align: "center" }));
  // series
  series.forEach((s) => {
    const rgb = _hexRgb(s.color || "#38bdf8");
    doc.setDrawColor(...rgb); doc.setLineWidth(s.width || 0.8);
    if (s.dashed) doc.setLineDashPattern([1.6, 1.2], 0);
    let prev = null;
    (s.points || []).forEach((p, i) => {
      if (p == null || !Number.isFinite(Number(p))) { prev = null; return; }
      const cx = sx(i), cy = sy(Number(p));
      if (prev) doc.line(prev[0], prev[1], cx, cy);
      prev = [cx, cy];
    });
    doc.setLineDashPattern([], 0);
    doc.setFillColor(...rgb);
    (s.points || []).forEach((p, i) => { if (p != null && Number.isFinite(Number(p))) doc.circle(sx(i), sy(Number(p)), 0.9, "F"); });
  });
  // legend
  let lx = plotX, ly = y + h + 4;
  doc.setFontSize(6.2);
  series.forEach((s) => {
    const rgb = _hexRgb(s.color || "#38bdf8");
    doc.setFillColor(...rgb); doc.rect(lx, ly - 2, 3, 3, "F");
    doc.setTextColor(60, 60, 60); doc.setFont("helvetica", "bold");
    const nm = String(s.name || "").slice(0, 26);
    doc.text(nm, lx + 4.5, ly + 0.4, { baseline: "middle" });
    lx += 6 + doc.getTextWidth(nm) + 5;
  });
  if (baseline != null) { doc.setDrawColor(255, 176, 0); doc.setLineWidth(0.5); doc.setLineDashPattern([1.4, 1.2], 0); doc.line(lx, ly - 0.5, lx + 5, ly - 0.5); doc.setLineDashPattern([], 0); doc.setTextColor(60, 60, 60); doc.text("Media Serie A", lx + 6.5, ly + 0.4, { baseline: "middle" }); }
  return ly + 4;
}

// Simple portrait formation pitch. slots: [{lx,ly,label,starter:{name,number}}] (lx 0-100 length GK->attack, ly 0-100 width)
export function drawFormationPitch(doc, x, y, w, h, slots = [], { teamName = "" } = {}) {
  // pitch surface
  doc.setFillColor(22, 92, 52); doc.rect(x, y, w, h, "F");
  doc.setDrawColor(255, 255, 255); doc.setLineWidth(0.4);
  doc.rect(x + 2, y + 2, w - 4, h - 4, "S");
  doc.line(x + 2, y + h / 2, x + w - 2, y + h / 2); // halfway
  doc.circle(x + w / 2, y + h / 2, Math.min(w, h) * 0.11, "S"); // centre circle
  // penalty boxes (GK at bottom, attack at top)
  const boxW = w * 0.44, boxH = h * 0.14;
  doc.rect(x + (w - boxW) / 2, y + h - 2 - boxH, boxW, boxH, "S"); // our goal (bottom)
  doc.rect(x + (w - boxW) / 2, y + 2, boxW, boxH, "S"); // top
  // players: lx small (GK) -> bottom, lx large (attack) -> top
  slots.forEach((s) => {
    const lx = Number(s.lx), ly = Number(s.ly);
    if (!Number.isFinite(lx) || !Number.isFinite(ly)) return;
    const cx = x + (ly / 100) * w;
    const cy = y + (1 - lx / 100) * h;
    const r = Math.min(w, h) * 0.05;
    doc.setFillColor(...NAVY); doc.setDrawColor(255, 209, 0); doc.setLineWidth(0.5);
    doc.circle(cx, cy, r, "FD");
    const num = s.starter?.number ?? s.number;
    doc.setTextColor(255, 209, 0); doc.setFont("helvetica", "bold"); doc.setFontSize(7);
    if (num != null && num !== "") doc.text(String(num), cx, cy + 1.2, { align: "center" });
    const nm = s.starter?.name || s.label || "";
    if (nm) { doc.setTextColor(255, 255, 255); doc.setFontSize(5.2); doc.text(String(nm).slice(0, 16), cx, cy + r + 2.6, { align: "center" }); }
  });
  if (teamName) { doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(6.5); doc.text(teamName, x + 3, y + 5); }
}

// Rough table height (mm) so we can keep a section title on the same page as its table.
export function estimateTableHeight(head, body, opts = {}) {
  const fs = (opts.styles && opts.styles.fontSize) || 8;
  const cp = (opts.styles && opts.styles.cellPadding) != null ? opts.styles.cellPadding : 1.8;
  const rowH = fs * 0.3528 * 1.15 + cp * 2 + 0.8;
  const headFs = (opts.headStyles && opts.headStyles.fontSize) || fs;
  const headH = headFs * 0.3528 * 2.2 + cp * 2 + 1; // headers may wrap to ~2 lines
  return headH + (body.length || 0) * rowH;
}

// sections: [{ title, head, body }]; radars: [{ title, axes, series }]; charts: [{ title, bars, unit }]
export async function exportTablesPdf({ title, subtitle, filename, sections, landscape = false, radars = [], charts = [], lineCharts = [], pitches = [], cover = false, coverMeta = "", coverNote = "", author = "", role = "", leadSections = [], trailingCharts = [], scatters = [] }) {
  const hasScatter = (scatters || []).some((s) => s && (s.points || []).length);
  if (![...sections, ...leadSections].some((s) => s.body.length) && !hasScatter) { toast.error("Nothing to export"); return; }
  const { crest, crestFull, mark, header, page } = await pdfBrand(landscape);
  const doc = new jsPDF({ orientation: landscape ? "landscape" : "portrait" });
  const dateStr = fmtDateNum(new Date());
  const authorLine = [author, role].filter(Boolean).join(", ");
  const headerMeta = [authorLine, dateStr].filter(Boolean).join("   ·   ");
  if (cover) {
    pdfCover(doc, { crest: crestFull, title, subtitle, note: coverNote, meta: authorLine, date: dateStr, landscape });
    doc.addPage();
  }
  pdfPageBg(doc, page);
  let y = pdfHeader(doc, title, subtitle, landscape, crest, header, headerMeta);
  const w = doc.internal.pageSize.getWidth();
  const drawSections = (list) => {
    const pageBot = landscape ? 192 : 282;
    const usableH = pageBot - 20;
    (list || []).forEach((s) => {
      if (!s.body.length) return;
      const titleH = s.title ? 10 : 0;
      const estH = estimateTableHeight(s.head, s.body, s.opts);
      const remaining = pageBot - y;
      let flow = "avoid";
      if (titleH + estH <= remaining) {
        // whole section fits below the current y — keep title + table together here
      } else if (titleH + estH <= usableH) {
        // fits on a fresh page — move title + table together (never orphan the heading)
        doc.addPage(); pdfPageBg(doc, page); y = 20;
      } else {
        // taller than a page — start under the title and let it flow across pages
        if (remaining < titleH + 28) { doc.addPage(); pdfPageBg(doc, page); y = 20; }
        flow = "auto";
      }
      if (s.title) y = pdfSectionTitle(doc, s.title, y);
      y = pdfTable(doc, s.head, s.body, y, { ...s.opts, pageBreak: flow });
    });
  };
  const drawCharts = (list) => {
    (list || []).filter((c) => c && (c.bars || []).length).forEach((c) => {
      const need = 14 + c.bars.length * 9;
      if (y + need > (landscape ? 185 : 270)) { doc.addPage(); pdfPageBg(doc, page); y = 20; }
      y = pdfSectionTitle(doc, c.title || "Chart", y);
      y = drawBarChart(doc, 16, y, w - 32, c.bars, { unit: c.unit ?? "€" }) + 6;
    });
  };
  drawSections(leadSections);
  const rads = (radars || []).filter((r) => r && r.axes && r.axes.length >= 3 && (r.series || []).length);
  if (rads.length) {
    y = pdfSectionTitle(doc, rads.length > 1 ? "Radar profiles" : (rads[0].title || "Radar profile"), y);
    const cols = Math.min(rads.length, landscape ? 2 : 1);
    const R = landscape ? (cols > 1 ? 30 : 34) : 42;
    const cyTop = y + R + 8;
    let maxBottom = cyTop;
    rads.forEach((r, i) => {
      const col = i % cols;
      const cx = (w / (cols + 1)) * (col + 1);
      const bottom = drawCompareRadar(doc, cx, cyTop, R, r.axes, r.series, cols > 1 ? r.title : null);
      if (bottom > maxBottom) maxBottom = bottom;
    });
    y = maxBottom + 8;
  }
  const chs = (charts || []).filter((c) => c && (c.bars || []).length);
  chs.forEach((c) => {
    const need = 14 + c.bars.length * 9;
    if (y + need > (landscape ? 185 : 270)) { doc.addPage(); pdfPageBg(doc, page); y = 20; }
    y = pdfSectionTitle(doc, c.title || "Chart", y);
    y = drawBarChart(doc, 16, y, w - 32, c.bars, { unit: c.unit ?? "€" }) + 6;
  });
  const lcs = (lineCharts || []).filter((c) => c && (c.series || []).length && (c.labels || []).length);
  lcs.forEach((c) => {
    const chartH = c.height || 46;
    const need = 12 + chartH + 12;
    if (y + need > (landscape ? 188 : 276)) { doc.addPage(); pdfPageBg(doc, page); y = 20; }
    y = pdfSectionTitle(doc, c.title || "Trend", y);
    y = drawLineChart(doc, 16, y, w - 32, chartH, { labels: c.labels, series: c.series, baseline: c.baseline }) + 8;
  });
  const pits = (pitches || []).filter((p) => p && (p.slots || []).length);
  pits.forEach((p) => {
    const pw = landscape ? 96 : 108, ph = landscape ? 120 : 150;
    const need = 12 + ph + 6;
    if (y + need > (landscape ? 188 : 276)) { doc.addPage(); pdfPageBg(doc, page); y = 20; }
    y = pdfSectionTitle(doc, p.title || "Formation", y);
    const px = (w - pw) / 2;
    drawFormationPitch(doc, px, y, pw, ph, p.slots, { teamName: p.teamName });
    y += ph + 8;
  });
  drawSections(sections);
  drawCharts(trailingCharts);
  // Scatter diagrams (e.g. Director cost-vs-performance) — one per page.
  const scs = (scatters || []).filter((s) => s && (s.points || []).length);
  if (scs.length) {
    const { drawScatterBlock } = await import("./scatterPdf");
    scs.forEach((s) => {
      doc.addPage(); pdfPageBg(doc, page); y = pdfHeader(doc, s.title || "Diagramma a dispersione", s.subtitle || "", landscape, crest, header, headerMeta);
      const sw = landscape ? w - 70 : w - 24;
      drawScatterBlock(doc, { x: 12, y: y + 2, w: sw, h: landscape ? 150 : 150, points: s.points, xLabel: s.xLabel, yLabel: s.yLabel, labelMode: "all", marker: s.marker || "dot" });
      if ((s.groups || []).length > 1) {
        let ly = y + 8; const lx = landscape ? w - 54 : w - 20;
        doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(11, 26, 51); doc.text("LEGENDA", lx, ly); ly += 5;
        doc.setFont("helvetica", "normal"); doc.setFontSize(7);
        s.groups.slice(0, 18).forEach((g) => { const h = (g.hex || "#0b1a33").replace("#", ""); doc.setFillColor(parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)); doc.circle(lx + 1.5, ly - 1, 1.3, "F"); doc.setTextColor(60, 60, 60); doc.text(String(g.label).slice(0, 22), lx + 5, ly); ly += 4.5; });
      }
    });
  }
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    if (cover && i === 1) continue;
    doc.setPage(i);
    pdfFooter(doc, { mark, landscape, right: `Confidential · All rights reserved · page ${i}/${pages}` });
  }
  pdfWatermarkAllPages(doc, crest, { skipFirst: cover });
  pdfFrameAllPages(doc, { skipFirst: cover });
  const shared = await finalizePdf(doc, filename, { title });
  if (!shared) toast.success("PDF exported");
}
